/* eslint-disable boundaries/element-types */
import { zodResolver } from '@hookform/resolvers/zod'
import {
  FormFieldsByUniqueID,
  MeasurementApplication,
  PermissionsCheck,
  SimpleFormField,
} from 'components/form/formTypes'
import {
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'features/auth'
import { isNumber, isString } from 'mathjs'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import {
  convertCoordinateString,
  ExternalUserFieldAccess,
  FormField,
  GeckoMeasurement,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
  InputType,
  UnitMagnitude,
} from 'types'
import { assetModelPrecision } from 'utils/constants'
import { z, ZodEffects, ZodTypeAny } from 'zod'

export function createZodSchemaObject(
  formFields: Record<string, FormField>
): z.AnyZodObject {
  const schemaDefinition: Record<
    string,
    ZodEffects<ZodTypeAny, unknown, unknown>
  > = {}
  Object.entries(formFields).forEach(([key, value]) => {
    if (value.validationRule) {
      schemaDefinition[key] = value.validationRule
    }
  })

  return z.object(schemaDefinition)
}

export const createEditedObject = (
  data: SimpleFormField,
  formFields: Record<string, FormField>,
  uniqueIDIndex: number = 1
) => {
  Object.keys(data).forEach((key) => {
    formFields[key].value = data[key]
  })
  const convertedFormFields = convertUniqueFormFieldsToFormFields(
    formFields,
    uniqueIDIndex
  )

  if (convertedFormFields.length === 0) {
    return [{ uniqueID: '', formFields: [] }]
  }

  return convertedFormFields
}

const convertUniqueFormFieldsToFormFields = (
  uniqueFormFields: Record<string, FormField>,
  uniqueIDIndex: number = 1
): FormFieldsByUniqueID[] => {
  const formFieldsMap: Record<string, FormField[]> = {}

  Object.keys(uniqueFormFields).forEach((key) => {
    const uniqueID = key.split('#')[uniqueIDIndex]
    if (!formFieldsMap[uniqueID]) {
      formFieldsMap[uniqueID] = []
    }
    formFieldsMap[uniqueID].push(uniqueFormFields[key])
  })

  return Object.keys(formFieldsMap).map((uniqueID) => ({
    uniqueID,
    formFields: formFieldsMap[uniqueID],
  }))
}

export function createUniqueFormFields(
  formFieldsByUniqueIDs: FormFieldsByUniqueID[]
): Record<string, FormField> {
  const uniqueFields: Record<string, FormField> = {}
  formFieldsByUniqueIDs.map((formFieldByID) => {
    const uniqueID = formFieldByID.uniqueID
    formFieldByID.formFields.map((formField) => {
      const fieldName = formField.dataKey
      const uniqueFieldName = `${fieldName}#${uniqueID}`
      uniqueFields[uniqueFieldName] = formField
    })
  })
  return uniqueFields
}

export const convertToMetricBaseUnit = (
  unitMagnitude: UnitMagnitude | GeckoMeasurementUnit | null
) => {
  /* This function converts the unit to the metric base unit that is stored in rolodex
    This needs to be a seperate function from convertToUnit becasue in the cases of InchesOrMillimeters we store the value in
    meters
*/
  const metricUnit = convertToUnit(GeckoMeasurementSystem.Metric, unitMagnitude)
  if (metricUnit === GeckoMeasurementUnit.Millimeters) {
    return GeckoMeasurementUnit.Meters
  }
  return metricUnit
}

export const convertToUnit = (
  measurementSystem: GeckoMeasurementSystem,
  unitMagnitude: UnitMagnitude | GeckoMeasurementUnit | null
) => {
  if (unitMagnitude === null) return null
  if (unitMagnitude instanceof GeckoMeasurementUnit) return unitMagnitude

  switch (measurementSystem) {
    case GeckoMeasurementSystem.Imperial:
      switch (unitMagnitude) {
        case UnitMagnitude.FeetOrMeters:
          return GeckoMeasurementUnit.Feet
        case UnitMagnitude.InchesOrMillimeters:
          return GeckoMeasurementUnit.Inches
        case UnitMagnitude.FahrenheitOrCelsius:
          return GeckoMeasurementUnit.Fahrenheit
        case UnitMagnitude.PsiOrMPa:
          return GeckoMeasurementUnit.PoundsForcePerSqInch
        case UnitMagnitude.Degrees:
          return GeckoMeasurementUnit.Degrees
        default:
          return GeckoMeasurementUnit.Feet
      }
    case GeckoMeasurementSystem.Metric:
      switch (unitMagnitude) {
        case UnitMagnitude.FeetOrMeters:
          return GeckoMeasurementUnit.Meters
        case UnitMagnitude.InchesOrMillimeters:
          return GeckoMeasurementUnit.Millimeters
        case UnitMagnitude.FahrenheitOrCelsius:
          return GeckoMeasurementUnit.Celsius
        case UnitMagnitude.PsiOrMPa:
          return GeckoMeasurementUnit.Megapascals
        case UnitMagnitude.Degrees:
          return GeckoMeasurementUnit.Degrees
        default:
          return GeckoMeasurementUnit.Meters
      }
  }
}

export const assetOverviewPermissionsCheck = (
  formField: FormField,
  userPermissions?: ReadonlySet<string> | undefined
) => {
  const isGecko =
    userPermissions?.has(PERMISSION_GECKO_INTERNAL_MEMBER) ?? false
  const hasUnitEditPermission =
    (userPermissions?.has(PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR) ||
      userPermissions?.has(PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN)) ??
    false
  const canEditField =
    isGecko ||
    (hasUnitEditPermission &&
      formField.externalUserAccess === ExternalUserFieldAccess.ReadWrite)
  const canReadField =
    formField.externalUserAccess !== ExternalUserFieldAccess.NoAccess || isGecko

  return { canEditField, canReadField }
}

export function isMissingDerivedLayerField(formItem: FormField) {
  return formItem.isNeededForDerivedLayer && formItem.value === undefined
}

export const applyMeasurementSystemToFormField = (
  formField: FormField,
  measurementSystem: GeckoMeasurementSystem
) => {
  const unitMagnitude = formField.unit_magnitude
  const units = convertToUnit(measurementSystem, unitMagnitude)
  const { fieldValue, fieldUnits } = getFieldValueAndUnit(
    formField.value,
    unitMagnitude,
    units,
    assetModelPrecision
  )
  return { fieldValue, fieldUnits, units }
}

export const generateToggleFieldsFromFormFields = (
  formFields: FormField[],
  uniqueID: string,
  measurementApplication?: MeasurementApplication,
  permissionsCheck?: PermissionsCheck
) => {
  return formFields.map((formField) => {
    const { fieldValue, fieldUnits, units } = measurementApplication
      ? applyMeasurementSystemToFormField(
          formField,
          measurementApplication?.measurementSystem
        )
      : { fieldValue: formField.value, fieldUnits: '', units: null }

    const { canReadField, canEditField } = permissionsCheck
      ? permissionsCheck.permissionsChecker(
          formField,
          permissionsCheck.userPermissions
        )
      : { canReadField: true, canEditField: true }

    return {
      formItemKey: `${formField.dataKey}#${uniqueID}`,
      formField: formField,
      formFieldValue: fieldValue,
      formFieldUnits: fieldUnits,
      unit_magnitude: formField.unit_magnitude,
      units,
      canReadField,
      canEditField,
      formDependencies: formField.dependencies,
      uniqueID,
    }
  })
}

const getFieldValueAndUnit = (
  selectionValue: string | number | string[] | undefined,
  unitMagnitude: UnitMagnitude | GeckoMeasurementUnit | null,
  displayUnit: GeckoMeasurementUnit | null,
  assetModelPrecision: number
) => {
  let fieldValue, fieldUnits
  const coordinateValue =
    typeof selectionValue === 'string' && displayUnit
      ? convertCoordinateString(
          selectionValue,
          GeckoMeasurementUnit.Meters,
          displayUnit
        )
      : null
  if (coordinateValue && displayUnit) {
    fieldValue = coordinateValue
    fieldUnits = displayUnit.name
  } else if (displayUnit && isNumber(selectionValue)) {
    //the asset model is always in meters or the metric equivalent for measurements like stress, rates etc
    const metricUnit = convertToMetricBaseUnit(unitMagnitude)
    fieldValue = GeckoMeasurement.CreateFromValue(
      selectionValue as number,
      metricUnit ?? GeckoMeasurementUnit.Meters
    )
      .convertTo(displayUnit)
      .toPrecision(assetModelPrecision).num
    if (
      unitMagnitude === UnitMagnitude.FeetOrMeters ||
      unitMagnitude === UnitMagnitude.InchesOrMillimeters ||
      unitMagnitude === UnitMagnitude.PsiOrMPa ||
      unitMagnitude === UnitMagnitude.FahrenheitOrCelsius
    ) {
      fieldValue = fieldValue.toFixed(5)
    }
    fieldUnits = displayUnit.name
  } else if (isString(selectionValue)) {
    fieldUnits = ''
  } else {
    fieldValue = selectionValue
    fieldUnits = displayUnit ? displayUnit.name : ''
  }

  return { fieldValue, fieldUnits }
}

export const createSimpleFormFields = (
  formFieldsByUniqueID: FormFieldsByUniqueID[]
): SimpleFormField => {
  const simplifiedFields = {} as SimpleFormField
  formFieldsByUniqueID.map((feature) => {
    feature.formFields.map((formField) => {
      const fieldValue =
        Array.isArray(formField.value) ||
        formField.value === undefined ||
        formField.value === null
          ? formField.value
          : (formField.value?.toString() ??
            formField.options?.at(0)?.value ??
            undefined)
      simplifiedFields[`${formField.dataKey}#${feature.uniqueID}`] = fieldValue
    })
  })
  return simplifiedFields
}

export function useGeckoForm(formFieldsByUniqueID: FormFieldsByUniqueID[]) {
  const schema = useMemo(() => {
    return createZodSchemaObject(createUniqueFormFields(formFieldsByUniqueID))
  }, [formFieldsByUniqueID])

  const simpleFormFields = useMemo(() => {
    return createSimpleFormFields(formFieldsByUniqueID)
  }, [formFieldsByUniqueID])

  const form = useForm({
    defaultValues: simpleFormFields,
    resolver: schema ? zodResolver(schema) : undefined,
    reValidateMode: 'onChange',
    mode: 'all',
  })

  useEffect(() => {
    const simpleFormFields = createSimpleFormFields(formFieldsByUniqueID)
    // Update values from current form state
    const currentValues = form.getValues()
    Object.keys(currentValues).forEach((fieldKey) => {
      if (currentValues[fieldKey]) {
        const [dataKey, uniqueID] = fieldKey.split('#')
        const formField = formFieldsByUniqueID
          .find((group) => group.uniqueID === uniqueID)
          ?.formFields.find((field) => field.dataKey === dataKey)

        if (formField?.inputType === InputType.Number) {
          simpleFormFields[fieldKey] = String(Number(currentValues[fieldKey]))
        } else {
          simpleFormFields[fieldKey] = currentValues[fieldKey]
        }
      }
    })

    form.reset(simpleFormFields)
  }, [form, formFieldsByUniqueID])

  return form
}

export function formatDropdownString(s: string | undefined): string {
  // Replace underscores with spaces, split into words, capitalize each word, and join back together
  return s
    ? s
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\B\w/g, (char) => char.toLowerCase())
    : ''
}
